import { Injectable, inject } from '@angular/core';
import { Observable, of, catchError, map, switchMap } from 'rxjs';
import type { SidebarMenuItem } from '@nebula-layout/layout';
import { HttpService } from './http/http.service';
import { AuthService } from './auth.service';
import { VaultOperativeRoleSyncService } from './vault-operative-role-sync.service';
import { syncRoleIdsIntoUserData } from './vault-menu-role-ids.util';
import {
  flattenVaultMenuRouteKeys,
  normalizeVaultMenuRouteKey,
  VAULT_MENU_CANONICAL,
} from './vault-menu/vault-menu-canonical.snapshot';
import {
  cloneVaultMenuForDisplay,
  collectAllowedRouteKeysFromApiMenu,
  resetVaultSidebarPermissionsPending,
  resolveVaultSidebarAllowedRouteKeys,
  setVaultSidebarAllowedRouteKeys,
} from './vault-menu/vault-sidebar-menu-merge.util';
import { VaultMenuAccessService } from './vault-menu/vault-menu-access.service';
import {
  getQaFallbackAllowedRouteKeys,
  resolveVaultQaRoleProfileForRestrictions,
  shouldStrictApplyVaultRoleRestrictions,
  userIsSuperadminFromSession,
} from './vault-menu/vault-role-menu-matrix.util';
import type { VaultSidebarMenuItem } from './vault-menu/vault-sidebar-menu.types';
import { resolveVaultMenuIcon } from './vault-menu/vault-menu-icons.util';
import { expandAllowedRouteKeysFromApiOptions } from './vault-menu/vault-menu-option-routes.util';

const LABELS_MAPPING_MENU: Record<string, string> = {
  SUB_MODULES_VAULT_HOME_NAME: 'Mi Tablero',
  SUB_MODULES_VAULT_DOCU_NAME: 'Centro Documental',
  OPTIONS_VAULT_INGEST: 'Carga de Documentos',
  OPTIONS_VAULT_PRESERVATION: 'Preservación Digital',
  OPTIONS_VAULT_RETENTION: 'Retención Documental',
  OPTIONS_VAULT_LEGAL_HOLD: 'Custodia Legal',
  OPTIONS_VAULT_REPORTS: 'Reportes Generales',
  OPTIONS_VAULT_CLOUD: 'Respaldo Externo',
  SUB_MODULES_VAULT_SYSTEM_NAME: 'Centro de control',
  OPTIONS_VAULT_NOTIFICATIONS: 'Notificaciones',
  OPTIONS_VAULT_AUDIT: 'Historial de Actividad',
  OPTIONS_VAULT_SETTINGS: 'Configuración General',
  SUB_MODULES_VAULT_ADMIN_NAME: 'Administrador',
  OPTIONS_VAULT_ADMIN_TENANTS: 'Gestión de Empresas',
  OPTIONS_VAULT_ADMIN_MODULE_PERMISSIONS: 'Permisos',
  OPTIONS_VAULT_ADMIN_USERS: 'Gestión de Usuarios',
  OPTIONS_VAULT_REPOSITORIO_ADMIN: 'Bóveda Digital',
  OPTIONS_VAULT_PRESERVATION_MESSAGES: 'Mensajes Informativos',
};

type MenuOptionGroup = {
  optionGroupId: string;
  optionGroupName: string;
  optionGroupAhrefIconClass?: string;
  optionGroupUrl?: string;
  options?: Array<{
    optionName: string;
    optionAhrefIconClass?: string;
    optionFullPath?: string;
    optionPath?: string;
  }>;
};

type MenuModule = {
  optionGroups?: MenuOptionGroup[];
};

type GroupedApplication = {
  applicationId?: string | number;
  applicationName?: string;
  moduleUrl?: string;
  modules?: unknown;
};

type GroupedRole = {
  applications?: GroupedApplication[];
};

@Injectable({ providedIn: 'root' })
export class VaultSidebarMenuService {
  private readonly http = inject(HttpService);
  private readonly auth = inject(AuthService);
  private readonly menuAccess = inject(VaultMenuAccessService);
  private readonly operativeRoleSync = inject(VaultOperativeRoleSyncService);
  private readonly MENU_ENDPOINT = '/simappe-admin/api/v1/menu';

  /** Menú lateral con snapshot canónico + restricciones según API / matriz QA. */
  getVaultSidebarMenu(options?: { skipRoleSync?: boolean }): Observable<VaultSidebarMenuItem[]> {
    resetVaultSidebarPermissionsPending();
    const sync$ = options?.skipRoleSync ? of(undefined) : this.ensureOperativeRoleSynced();
    return sync$.pipe(
      switchMap(() => this.fetchApiMenuTree()),
      map(({ apiTree, allowed }) => this.buildDisplayMenu(apiTree, allowed)),
      catchError(() => of(this.getCanonicalFallbackMenu())),
    );
  }

  /** Sidebar completo (fallback si falla el API). */
  getCanonicalFallbackMenu(): VaultSidebarMenuItem[] {
    const allowed = userIsSuperadminFromSession()
      ? flattenVaultMenuRouteKeys(VAULT_MENU_CANONICAL)
      : resolveVaultSidebarAllowedRouteKeys(new Set(), []);
    this.menuAccess.setAllowedRouteKeys(allowed);
    const canonical = structuredClone(VAULT_MENU_CANONICAL) as VaultSidebarMenuItem[];
    return cloneVaultMenuForDisplay(canonical);
  }

  getAllowedRouteKeys(): Observable<Set<string>> {
    resetVaultSidebarPermissionsPending();
    return this.ensureOperativeRoleSynced().pipe(
      switchMap(() => this.fetchApiMenuTree()),
      map(({ apiTree, allowed }) => {
        const keys = this.finalizeAllowedRouteKeys(apiTree, allowed);
        this.menuAccess.setAllowedRouteKeys(keys);
        return keys;
      }),
    );
  }

  /** Menú crudo del API (sin merge con snapshot). */
  getApiMenu(): Observable<SidebarMenuItem[]> {
    return this.fetchApiMenuTree().pipe(map(({ apiTree }) => apiTree));
  }

  private ensureOperativeRoleSynced(): Observable<void> {
    return this.operativeRoleSync.syncFromApi();
  }

  private fetchApiMenuTree(): Observable<{ apiTree: SidebarMenuItem[]; allowed: Set<string> }> {
    syncRoleIdsIntoUserData();
    let roleIds = this.auth.getMenuRoleIds();
    if (!roleIds.length) {
      syncRoleIdsIntoUserData();
      roleIds = this.auth.getMenuRoleIds();
    }
    if (!roleIds.length) {
      return of({ apiTree: [] as SidebarMenuItem[], allowed: new Set<string>() });
    }

    const selectedProductId = localStorage.getItem('selectedProductId');

    return this.http
      .postContent<unknown>(`${this.MENU_ENDPOINT}/read-by-roles-grouped`, roleIds)
      .pipe(
        map((content) => {
          const items = Array.isArray(content) ? content : [];
          if (!items.length) {
            return { apiTree: [] as SidebarMenuItem[], allowed: new Set<string>() };
          }

          const normalized = this.normalizeGroupedRoles(items);
          let filtered = this.filterByApplicationId(normalized, selectedProductId);
          if (!filtered.length && selectedProductId) {
            filtered = normalized;
          }

          let tree = this.buildMenuTree(filtered);
          if (!tree.length && filtered !== normalized) {
            tree = this.buildMenuTree(normalized);
          }

          const allowed = this.buildAllowedRouteKeys(tree);
          return { apiTree: tree, allowed };
        }),
      );
  }

  private buildDisplayMenu(
    apiTree: SidebarMenuItem[],
    apiAllowed: Set<string>,
  ): VaultSidebarMenuItem[] {
    const allowed = this.finalizeAllowedRouteKeys(apiTree, apiAllowed);
    this.menuAccess.setAllowedRouteKeys(allowed);

    return cloneVaultMenuForDisplay(
      structuredClone(VAULT_MENU_CANONICAL) as VaultSidebarMenuItem[],
    );
  }

  private finalizeAllowedRouteKeys(apiTree: SidebarMenuItem[], apiAllowed: Set<string>): Set<string> {
    if (userIsSuperadminFromSession()) {
      return flattenVaultMenuRouteKeys(VAULT_MENU_CANONICAL);
    }

    let allowed = resolveVaultSidebarAllowedRouteKeys(apiAllowed, apiTree);
    if (apiTree.length > 0) {
      allowed = this.buildAllowedRouteKeys(apiTree, allowed);
    }
    if (shouldStrictApplyVaultRoleRestrictions()) {
      allowed = this.capAllowedRoutesForRoleProfile(allowed);
    }
    return allowed;
  }

  /** Si el API devuelve más opciones de las del rol, limitar (p. ej. consulta sin retención). */
  private capAllowedRoutesForRoleProfile(allowed: Set<string>): Set<string> {
    if (userIsSuperadminFromSession()) {
      return flattenVaultMenuRouteKeys(VAULT_MENU_CANONICAL);
    }

    const profile = resolveVaultQaRoleProfileForRestrictions();
    if (profile === 'admin') return allowed;

    const cap = getQaFallbackAllowedRouteKeys(profile);
    if (cap.size === 0) return allowed;

    const capped = new Set<string>();
    for (const key of cap) {
      if (allowed.has(key) || this.routeAllowedBySet(key, allowed)) {
        capped.add(key);
      }
    }
    for (const key of allowed) {
      if (this.routeAllowedBySet(key, cap)) {
        capped.add(key);
      }
    }
    return capped.size > 0 ? capped : allowed;
  }

  private routeAllowedBySet(route: string, allowed: Set<string>): boolean {
    const normalized = normalizeVaultMenuRouteKey(route);
    if (!normalized) return false;
    if (allowed.has(normalized)) return true;
    const head = normalized.replace(/^\/layout\/vault\/?/, '').split('/')[0];
    if (!head) return false;
    for (const key of allowed) {
      const keyHead = key.replace(/^\/layout\/vault\/?/, '').split('/')[0];
      if (keyHead === head) return true;
    }
    return false;
  }

  private asUnrestricted(item: SidebarMenuItem): VaultSidebarMenuItem {
    return {
      ...item,
      vaultRestricted: false,
      children: item.children?.map((c) => this.asUnrestricted(c)),
    };
  }

  private normalizeGroupedRoles(items: unknown[]): GroupedRole[] {
    return items.map((role) => {
      const r = role as GroupedRole;
      if (!Array.isArray(r.applications)) return r;
      return {
        ...r,
        applications: r.applications.map((app) => this.normalizeApplication(app)),
      };
    });
  }

  private normalizeApplication(app: GroupedApplication): GroupedApplication {
    return {
      ...app,
      modules: this.coerceModules(app.modules),
    };
  }

  private coerceModules(modules: unknown): MenuModule[] {
    if (modules == null) return [];

    if (typeof modules === 'string') {
      const trimmed = modules.trim();
      if (!trimmed) return [];
      try {
        return this.coerceModules(JSON.parse(trimmed) as unknown);
      } catch {
        return [];
      }
    }

    if (Array.isArray(modules)) {
      return modules.map((mod) => {
        const m = mod as MenuModule & { optionGroups?: unknown };
        let optionGroups = m.optionGroups;
        if (typeof optionGroups === 'string') {
          try {
            optionGroups = JSON.parse(optionGroups) as MenuOptionGroup[];
          } catch {
            optionGroups = [];
          }
        }
        return {
          ...m,
          optionGroups: Array.isArray(optionGroups) ? optionGroups : [],
        };
      });
    }

    if (typeof modules === 'object') {
      return this.coerceModules([modules]);
    }

    return [];
  }

  private filterByApplicationId(items: GroupedRole[], selectedProductId: string | null): GroupedRole[] {
    if (!selectedProductId) return items;

    const filtered: GroupedRole[] = [];
    for (const role of items) {
      if (!role.applications?.length) continue;

      const apps = this.pickVaultApplications(role.applications, selectedProductId);
      if (apps.length) filtered.push({ ...role, applications: apps });
    }
    return filtered;
  }

  private pickVaultApplications(
    applications: GroupedApplication[],
    selectedProductId: string,
  ): GroupedApplication[] {
    const byId = applications.filter(
      (app) => app.applicationId?.toString() === selectedProductId,
    );
    if (byId.length) return byId;

    const byVaultHint = applications.filter((app) => {
      const name = (app.applicationName ?? '').toLowerCase();
      const url = (app.moduleUrl ?? '').toLowerCase();
      return (
        name.includes('vault') ||
        name.includes('nebula') ||
        name.includes('bóveda') ||
        name.includes('boveda') ||
        url.includes('vault')
      );
    });
    if (byVaultHint.length) return byVaultHint;

    return applications;
  }

  private buildMenuTree(items: GroupedRole[]): SidebarMenuItem[] {
    const menuItems: SidebarMenuItem[] = [];
    const processedGroups = new Set<string>();

    for (const role of items) {
      if (!role.applications) continue;

      for (const app of role.applications) {
        const modules = this.coerceModules(app.modules);
        if (!modules.length) continue;

        for (const mod of modules) {
          if (!mod.optionGroups?.length) continue;
          for (const group of mod.optionGroups) {
            const groupKey = `${group.optionGroupId}`;
            if (processedGroups.has(groupKey)) continue;
            processedGroups.add(groupKey);

            const groupLabel = this.getLabel(group.optionGroupName);
            const groupItem: SidebarMenuItem = {
              label: groupLabel,
              icon: resolveVaultMenuIcon(
                group.optionGroupAhrefIconClass,
                groupLabel,
                group.optionGroupName,
              ),
              route: group.optionGroupUrl,
              children: [],
              expanded: false,
            };

            if (group.options?.length) {
              for (const opt of group.options) {
                const optLabel = this.getLabel(opt.optionName);
                groupItem.children!.push({
                  label: optLabel,
                  icon: resolveVaultMenuIcon(
                    opt.optionAhrefIconClass,
                    optLabel,
                    opt.optionName,
                  ),
                  route: opt.optionFullPath || opt.optionPath,
                  children: [],
                  optionKey: opt.optionName,
                } as VaultSidebarMenuItem);
              }
            }
            menuItems.push(groupItem);
          }
        }
      }
    }

    return menuItems;
  }

  private buildAllowedRouteKeys(
    apiTree: SidebarMenuItem[],
    seed?: Set<string>,
  ): Set<string> {
    let keys = seed ? new Set(seed) : collectAllowedRouteKeysFromApiMenu(apiTree);
    const optionKeys: string[] = [];
    const walk = (items: SidebarMenuItem[]) => {
      for (const item of items) {
        const ok = (item as VaultSidebarMenuItem & { optionKey?: string }).optionKey;
        if (ok) optionKeys.push(ok);
        if (item.children?.length) walk(item.children);
      }
    };
    walk(apiTree);
    keys = expandAllowedRouteKeysFromApiOptions(keys, optionKeys);
    return keys;
  }

  private getLabel(key?: string): string {
    if (!key) return '';
    return LABELS_MAPPING_MENU[key] ?? this.formatKey(key);
  }

  private formatKey(key: string): string {
    return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }
}
